// MapaEmpreendimentos.tsx
import React, { useState, useEffect, useRef } from 'react';
import Map, { Source, Marker, Layer } from 'react-map-gl';
import Select from 'react-select';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, Home, Building2, Box, Briefcase, Layers, Flame, FileText, Circle, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import '../../styles/mapaStyles/MapaEmpreendimentos.css';
import HeatMapEmpreendimentos from './HeatMapEmpreendimentos';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import Loading from "../Loading";
import { ResumoMapa } from '../../API/mapasService';
import api from '../../API/api';
import { getColorByCnae, getIconByCnae } from './MapaComercio';
import LegendaCnae from "./LegendaCnae";

export interface Imovel {
  id: string;
  latitude: number;
  longitude: number;
  nome: string;
  tipo?: string[];
  bairro?: string;
  quartos?: number;
  construtora?: string;
  preco?: number;
  unidadesTotais?: number;
  vendidosPercent?: number;
  status?: string;
}

export interface MunicipioFeature {
  type: 'Feature';
  geometry: any;
  properties: {
    id: string;
    nome: string;
    uf: string;
    densidade_demografica?: number;
  };
}

export interface ComercioMarker  {
  id: number;
  cnaes_secundarios: {
    codigo: string;
    descricao: string;
  }[];
  cnae_principal: {
    codigo: string;
    descricao: string;
  };
  cnae_codigo: string;
  cnpj: string;
  nome_fantasia: string;
  situacao_cadastral: number;
  data_situacao_cadastral: string;
  motivo_situacao_cadastral: string;
  data_inicio_atividade: string;
  telefone_principal: string;
  telefone_secundario: string;
  email: string;
  latitude: number;
  longitude: number;
  descricao_endereco: string;
  municipio: string;
  bairro: string;
  location: string;

  distancia?: number;
  categoria?: string;
  endereco?: string;
}

export interface ComercioSimples {
  id: number;
  nome_fantasia: string;
  latitude: number;
  longitude: number;
  cnae_codigo: string;
}

export interface MapaEmpreendimentosProps {
  imoveis: Imovel[];
  municipios?: MunicipioFeature[];
  loading: boolean;
  initialViewState?: { latitude: number; longitude: number; zoom?: number };
  onPoligonoEmpreendimentos?: (geojson: any) => void;
  onPoligonoComercios?: (geojson: any) => void;
  onUpdateComercios?: (lista: ComercioSimples[]) => void;
  resumo?: ResumoMapa | null;
  comercios?: ComercioSimples[]; 
}

const MAX_AREA = 5_000_000; // 5 km²

const MapaEmpreendimentos: React.FC<MapaEmpreendimentosProps> = ({
  imoveis,
  resumo,
  municipios = [],
  loading,
  initialViewState,
  onPoligonoEmpreendimentos,
  onPoligonoComercios,
  onUpdateComercios,
  comercios = [], 
}) => {
  const [viewState, setViewState] = useState({
    latitude: -8.05,
    longitude: -34.9,
    zoom: 11,
  });

  const [heatmapOn, setHeatmapOn] = useState(false);
  const [imovelSelecionado, setImovelSelecionado] = useState<Imovel | null>(null);
  const [poligonos, setPoligonos] = useState<any[]>([]);
  const [poligonoEnviado, setPoligonoEnviado] = useState(false);
  const [drawMode, setDrawMode] = useState<'polygon' | 'circle'>('polygon');
  const [circleDrawing, setCircleDrawing] = useState(false);

  // novo estado para guardar o polígono recém desenhado
  const [poligonoGeojson, setPoligonoGeojson] = useState<any | null>(null);

  // popup para escolher filtro
  const [mostrarPopupFiltroPoligono, setMostrarPopupFiltroPoligono] = useState(false);


  const [mostrarResumo, setMostrarResumo] = useState(true); // controla exibição do card de resumo

  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const resumoRef = useRef<HTMLDivElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipLeft, setTooltipLeft] = useState(0);
  const [tooltipText, setTooltipText] = useState('');

  const mapRef = useRef<any>(null);
  const drawRef = useRef<MapboxDraw | null>(null);

  // --- Estados para círculo ---
  const [circleCenter, setCircleCenter] = useState<[number, number] | null>(null);
  const [circleRadius, setCircleRadius] = useState<number>(1); // km inicial
  const [circleFeatureId, setCircleFeatureId] = useState<string | null>(null);
  const [showRadiusPopup, setShowRadiusPopup] = useState(false);

  // --- Comercios ---
  const [comercioSelecionado, setComercioSelecionado] = useState<ComercioSimples | null>(null);
  const [detalhes, setDetalhes] = useState<ComercioMarker | null>(null);
  const [popupPos, setPopupPos] = useState<{ x: number; y: number } | null>(null);
  const [poligono, setPoligono] = useState<any>(null);
  const [mostrarLegenda, setMostrarLegenda] = useState(false);


  // Estados para filtro de CNAE
  const [cnaes, setCnaes] = useState<{ label: string; value: string }[]>([]);
  const [selectedCnae, setSelectedCnae] = useState<{ label: string; value: string }[]>([]);
  const [mostrarPopupFiltro, setMostrarPopupFiltro] = useState(false);
  const cnaeQuery = selectedCnae.map(c => c.value).join(",");
  const [viewportTravado, setViewportTravado] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState(false);
  const [primaryCnaeOnly, setPrimaryCnaeOnly] = useState(false);

  const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
  // Defina a variável do zoom mínimo para habilitar clique
  const CLICKABLE_ZOOM = 13;

  const handleLimparComercios = () => {
    onUpdateComercios?.([]); 
    setSelectedCnae([]);     
    setFiltroAtivo(false);   
    setMostrarPopupFiltro(false);
  };

  const handleComercioClick = (comercio: ComercioSimples) => {
    setComercioSelecionado(comercio);

    if (mapRef.current) {
      const point = mapRef.current.project([comercio.longitude, comercio.latitude]);
      setPopupPos({ x: point.x, y: point.y });
    }
  };

  const getMapaFrame = () => {
    if (!mapRef.current) return null;

    const bounds = mapRef.current.getBounds();
    const frame = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
      zoom: mapRef.current.getZoom(),
    };

    return frame;
  };

  // Função para buscar CNAEs
  const buscarCnaes = async () => {
    try {
      const { data } = await api.get("/CnaeViewSet", { params: { sem_paginacao: true } });
      const opcoes = data.map((c: { codigo: string; descricao: string }) => ({
        label: `${c.codigo} - ${c.descricao}`,
        value: c.codigo,
      }));
      setCnaes(opcoes);
    } catch (err) {
      console.error("Erro ao buscar CNAEs:", err);
    }
  };

  const enviarPoligonoComCnae = async () => {
    // Se não tiver polígono, simplesmente sai
    if (!poligonoGeojson) return;

    try {
      const geojsonSemCnae = { ...poligonoGeojson };
      delete geojsonSemCnae.cnae;

      // Se não houver CNAE selecionado -> envia string vazia
      const cnaeString =
        selectedCnae.length > 0
          ? selectedCnae.map((c) => c.value).join(",")
          : "";

      const { data } = await api.post(
        `/EstabelecimentoViewSet/por_poligono/?cnae=${cnaeString}`,
        geojsonSemCnae
      );

      onUpdateComercios?.(
        data.map((c: any) => ({
          id: c.id,
          nome_fantasia: c.nome_fantasia,
          latitude: Number(c.latitude),
          longitude: Number(c.longitude),
          cnae_codigo: c.cnae_codigo,
        }))
      );
    } catch (err) {
      console.error("Erro ao enviar polígono + CNAE:", err);
      alert("Erro ao buscar comércios com o filtro aplicado.");
    }
  };


  // Atualiza os empreendimentos conforme muda a viewport ou o CNAE selecionado
  useEffect(() => {
    // Só ativa depois de clicar em Filtrar
    if (!filtroAtivo) return;         
    if (viewportTravado) return;

    if (fetchTimeout.current) clearTimeout(fetchTimeout.current);

    fetchTimeout.current = setTimeout(async () => {
      const frame = getMapaFrame();
      if (!frame) return;

      try {
        const cnaeQuery =
          selectedCnae.length > 0
            ? selectedCnae.map(c => c.value).join(",")
            : "";

        const { data } = await api.get("/EstabelecimentoViewSet/viewport/", {
          params: {
            north: frame.north,
            south: frame.south,
            east: frame.east,
            west: frame.west,
            cnae: cnaeQuery,
            primary_cnae_only: primaryCnaeOnly,
          },
        });

        const lista: ComercioSimples[] = data.map((item: any) => ({
          id: item.id,
          nome_fantasia: item.nome_fantasia,
          latitude: Number(item.latitude),
          longitude: Number(item.longitude),
          cnae_codigo: item.cnae_codigo,
        }));

        onUpdateComercios?.(lista);
      } catch (err) {
        console.error("Erro ao atualizar empreendimentos por CNAE:", err);
      }
    }, 500);

    return () => {
      if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
    };
  }, [viewState, selectedCnae, filtroAtivo]);


  useEffect(() => {
    if (!comercioSelecionado || !mapRef.current) return;

    const updatePopup = () => {
      const point = mapRef.current.project([
        comercioSelecionado.longitude,
        comercioSelecionado.latitude
      ]);
      setPopupPos({ x: point.x, y: point.y });
    };

    mapRef.current.on("move", updatePopup);
    mapRef.current.on("zoom", updatePopup);

    return () => {
      mapRef.current.off("move", updatePopup);
      mapRef.current.off("zoom", updatePopup);
    };
  }, [comercioSelecionado]);

  const limparGeojson = (geojson: any) => ({
    type: "FeatureCollection",
    features: geojson?.features ?? []
  });

  // --------------------------------------------------------------------------------

  useEffect(() => {
    if (initialViewState) {
      setViewState(prev => ({
        ...prev,
        latitude: initialViewState.latitude,
        longitude: initialViewState.longitude,
        zoom: initialViewState.zoom ?? prev.zoom,
      }));
    }
  }, [initialViewState]);

  useEffect(() => {
    if (municipios.length === 0 || initialViewState) return;

    const mun = municipios[0];
    if (!mun.geometry || !mun.geometry.coordinates) return;

    const coords = mun.geometry.coordinates.flat(2);
    const lons = coords.filter((_: number, i: number) => i % 2 === 0);
    const lats = coords.filter((_: number, i: number) => i % 2 === 1);
    const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;

    setViewState(prev => ({
      ...prev,
      longitude: centerLon,
      latitude: centerLat,
      zoom: 12,
    }));
  }, [municipios, initialViewState]);

  useEffect(() => {
    if (!heatmapOn && mapRef.current && !drawRef.current) {
      const draw = new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true } });
      drawRef.current = draw;
      mapRef.current.addControl(draw);
      mapRef.current.on('draw.create', handleDrawChange);
      mapRef.current.on('draw.update', handleDrawChange);
      mapRef.current.on('draw.delete', handleDrawChange);
    }
  }, [heatmapOn]);

  useEffect(() => {
    return () => {
      if (mapRef.current && drawRef.current) {
        drawRef.current = null;
      }
    };
  }, []);


  const handleProgressMouseMove = (e: React.MouseEvent<HTMLDivElement>, imovel: Imovel) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setTooltipLeft(x);

    const unidades = imovel.unidadesTotais ?? 0;
    const percent = imovel.vendidosPercent ?? 0;
    const vendidas = Math.round((unidades * percent) / 100) || 0;
    const disponiveis = unidades - vendidas;
    setTooltipText(`Vendidas: ${vendidas} | Disponíveis: ${disponiveis}`);
  }

  const handleDrawChange = () => {
    if (!drawRef.current) return;
    const data = drawRef.current.getAll();
    setPoligonos(data.features);
  };

  const handleEnviarPoligonos = () => {
    const cnaeQuery = selectedCnae.map(c => c.value).join(",");
    let featuresToSend = poligonos;

    // Recuperar do Draw caso ainda não esteja em estado
    if ((!featuresToSend || featuresToSend.length === 0) && drawRef.current) {
      try {
        const data = drawRef.current.getAll();
        featuresToSend = data.features || [];
        setPoligonos(featuresToSend);
      } catch (err) {
        console.warn('Erro ao obter desenhos do drawRef antes de enviar', err);
        featuresToSend = [];
      }
    }

    // Nenhum polígono desenhado
    if (!featuresToSend || featuresToSend.length === 0) {
      alert("Nenhum polígono desenhado!");
      return;
    }

    // Validar área
    for (const feature of featuresToSend) {
      const area = turf.area(feature);
      if (area > MAX_AREA) {
        alert(`Um dos polígonos ultrapassa o limite de ${(MAX_AREA / 1_000_000).toFixed(1)} km²`);
        return;
      }
    }

    // Construção do objeto GeoJSON
    const geojson = { 
      type: "FeatureCollection", 
      features: featuresToSend,
      cnae: cnaeQuery 
    };

    // Armazena o polígono atual que será enviado após escolha do popup
    setPoligonoGeojson(geojson);

    // Abre popup de escolha: Empreendimentos / Comércios / Ambos
    setMostrarPopupFiltroPoligono(true);

    // Travar viewport e limpar desenho
    setViewportTravado(true);
    setPoligonoEnviado(true);

    if (drawRef.current) drawRef.current.deleteAll();
    setPoligonos([]);

    if (initialViewState) {
      setViewState({
        latitude: initialViewState.latitude,
        longitude: initialViewState.longitude,
        zoom: initialViewState.zoom ?? 11
      });
    }
  };

  const handleVoltar = () => window.location.reload();

  const handleMapLoad = (event: any) => {
    const map = event.target;
    if (!map) return;

    mapRef.current = map;

    // 🔹 Desativa o zoom padrão de rolagem (mesmo após o estilo carregar)
    map.scrollZoom.disable();

    // 🔹 Reforça a desativação após o estilo estar pronto
    map.on("style.load", () => {
      map.scrollZoom.disable();
    });

    // 🔹 Adiciona comportamento personalizado de Ctrl + Scroll
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault(); // impede o scroll da página
        const zoomDelta = e.deltaY * -0.01;
        const around = map.unproject([e.offsetX, e.offsetY]);
        map.zoomTo(map.getZoom() + zoomDelta, { around });
      }
    };

    const canvas = map.getCanvas();
    canvas.addEventListener("wheel", handleWheel, { passive: false });

    // 🔹 Inicializa MapboxDraw (se for usado)
    if (!heatmapOn && !drawRef.current) {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: { polygon: true, trash: true },
      });

      try {
        map.addControl(draw);
        drawRef.current = draw;

        map.on("draw.create", handleDrawChange);
        map.on("draw.update", handleDrawChange);
        map.on("draw.delete", handleDrawChange);
      } catch (e) {
        console.warn("Erro ao inicializar MapboxDraw:", e);
      }
    }

    // 🔹 Remove o listener ao desmontar o mapa
    return () => {
      canvas.removeEventListener("wheel", handleWheel);
    };
  };

  const getIconByTipo = (tipos?: string[]) => {
    if (!tipos || tipos.length === 0) return MapPin;
    if (tipos.length > 1) return MapPin;
    switch (tipos[0].toLowerCase()) {
      case 'casa': return Home;
      case 'apartamento': return Building2;
      case 'compacto': return Box;
      case 'comercial': return Briefcase;
      case 'lote': return Layers;
      default: return MapPin;
    }
  };

  // --- Função para criar/atualizar círculo ---
  const updateCircle = (center: [number, number], radius: number) => {
    if (!mapRef.current || !drawRef.current) return;
    const map = mapRef.current;
    if (!map || !drawRef.current) return;

    try {
      if (circleFeatureId) drawRef.current.delete(circleFeatureId);
      const circle = turf.circle(center, radius, { steps: 64, units: 'kilometers' });
      const [id] = drawRef.current.add(circle) as string[];
      setCircleFeatureId(id);
    } catch (err) {
      console.warn('Erro ao atualizar círculo:', err);
    }
  };

  // --- Modo de desenho do círculo ---
  useEffect(() => {
    if (!circleDrawing || !mapRef.current) return;
    const map = mapRef.current;

    const handleClick = (e: any) => {
      const clickedPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      if (circleCenter && circleFeatureId) {
        // cria polígono do círculo atual
        const circlePoly = turf.circle(circleCenter, circleRadius, { steps: 64, units: 'kilometers' });
        const pt = turf.point(clickedPoint);

        // verifica se o clique está dentro do círculo
        if (turf.booleanPointInPolygon(pt, circlePoly)) {
          // remove círculo
          drawRef.current?.delete(circleFeatureId);
          setCircleFeatureId(null);
          setCircleCenter(null);
          setShowRadiusPopup(false);
          return;
        }
      }

      // se não clicou dentro de um círculo existente, cria/atualiza o círculo
      setCircleCenter(clickedPoint);
      updateCircle(clickedPoint, circleRadius);
      setShowRadiusPopup(true);
    };

    map.on('click', handleClick);
    return () => map.off('click', handleClick);
  }, [circleDrawing, circleFeatureId]);

  // --- Atualiza posição do círculo ao arrastar ---
  useEffect(() => {
    if (!drawRef.current || !circleFeatureId || !mapRef.current) return;
    const map = mapRef.current;
    map.on('draw.update', (e: any) => {
      const feature = e.features?.[0];
      if (feature && feature.geometry.type === 'Polygon') {
        const center = turf.center(feature).geometry.coordinates as [number, number];
        setCircleCenter(center);
      }
    });
    return () => map.off('draw.update');
  }, [circleFeatureId]);

  if (loading) return <Loading />;

  return (
    <div className="mapa-root">
      {/* --- TOOLBAR SUPERIOR --- */}
      <div className="mapa-toolbar">
        {poligonoEnviado ? (
          <button onClick={handleVoltar} className="mapa-btn-enviar">Voltar</button>
        ) : (
          <button onClick={handleEnviarPoligonos} className="mapa-btn-enviar">Enviar polígonos</button>
        )}
      </div>

      {/* --- ICONES --- */}
      <div className="mapa-icons-card">
        <Layers
          size={24}
          color={mostrarLegenda ? '#29306a' : '#666'}
          style={{ cursor: 'pointer' }}
          onClick={() => setMostrarLegenda(prev => !prev)}
        />
        <Store
          size={24}
          color={mostrarPopupFiltro ? '#29306a' : '#666'}
          style={{ cursor: 'pointer' }}
          onClick={async () => {
            setMostrarPopupFiltro(prev => !prev);
            if (!cnaes.length) await buscarCnaes();
          }}
        />
        <Flame
          size={24}
          color={heatmapOn ? '#ff4d4f' : '#666'}
          style={{ cursor: 'pointer' }}
          onClick={() => setHeatmapOn(!heatmapOn)}
        />
        <FileText
          size={24}
          color={mostrarResumo ? '#29306a' : '#666'}
          style={{ cursor: 'pointer' }}
          onClick={() => setMostrarResumo(true)}
        />
        <Circle
          size={24}
          color={circleDrawing ? '#29306a' : '#666'}
          style={{ cursor: 'pointer' }}
          onClick={() => setCircleDrawing(prev => !prev)}
        />
      </div>

      {/* --- POPUP DE RAIO --- */}
      {showRadiusPopup && circleCenter && (
        <div className="mapa-radius-popup">
          <h4>Escolha o raio do círculo (0–5 km)</h4>
          <input
            type="range"
            min={0}
            max={5}
            step={0.1}
            value={circleRadius}
            onChange={(e) => {
              const newRadius = parseFloat(e.target.value);
              setCircleRadius(newRadius);
              if (circleCenter) updateCircle(circleCenter, newRadius);
            }}
          />
          <div className="radius-value">{circleRadius.toFixed(1)} km</div>
          <button onClick={() => setShowRadiusPopup(false)}>OK</button>
        </div>
      )}

      {/* --- BOTÕES ZOOM --- */}
      <div style={{
        position: "absolute",
        bottom: 20,
        right: 20,
        display: "flex",
        flexDirection: "column",
        gap: 5,
        zIndex: 10,
      }}>
        <button onClick={() => mapRef.current?.zoomIn()} style={{
          background: "white",
          border: "1px solid #ccc",
          borderRadius: "8px",
          width: "36px",
          height: "36px",
          fontSize: "20px",
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}>+</button>
        <button onClick={() => mapRef.current?.zoomOut()} style={{
          background: "white",
          border: "1px solid #ccc",
          borderRadius: "8px",
          width: "36px",
          height: "36px",
          fontSize: "20px",
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}>−</button>
      </div>

          {detalhes && (
            <div className="card-detalhes">
              <h3>{detalhes.nome_fantasia}</h3>
              <p><strong>CNPJ:</strong> {detalhes.cnpj}</p>
              <p><strong>Email:</strong> {detalhes.email}</p>
              <p><strong>Telefone:</strong> {detalhes.telefone_principal}</p>
              <p><strong>Início das atividades:</strong> {detalhes.data_inicio_atividade}</p>
              <p><strong>Endereço:</strong> {detalhes.descricao_endereco}</p>
              <p><strong>Bairro:</strong> {detalhes.bairro}</p>
              <p><strong>Município:</strong> {detalhes.municipio}</p>
              <p><strong>Categoria:</strong> {detalhes.cnae_principal?.descricao ?? 'Não informado'}</p>

              {/* Botão para fechar o card de detalhes */}
              <button onClick={() => setDetalhes(null)}>Fechar</button>
            </div>
          )}

          {mostrarPopupFiltro && (
            <div className="mapa-popup-filtro">
              <h4>Filtrar por CNAE</h4>
              <Select
                isMulti
                options={cnaes}
                value={selectedCnae}
                onChange={(values) => setSelectedCnae(values as any)}
                placeholder="Selecione um ou mais CNAEs..."
              />
              <label style={{ display: "flex", alignItems: "center", marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={primaryCnaeOnly}
                  onChange={(e) => setPrimaryCnaeOnly(e.target.checked)}
                  style={{ marginRight: 5 }}
                />
                Buscar apenas CNAE principal
              </label>
              <button
                onClick={() => {
                  enviarPoligonoComCnae();
                  setFiltroAtivo(true);
                  setMostrarPopupFiltro(false);
                }}
              >
                Filtrar
              </button>
              <button  onClick={handleLimparComercios}>
                Limpar
              </button>
              <button onClick={() => setMostrarPopupFiltro(false)}>Cancelar</button>
            </div>
          )}

          {mostrarPopupFiltroPoligono && (
            <div className="popup-overlay">
              <div className="popup-content-overlay">
                <h3>Filtrar por:</h3>

                <button
                  onClick={ () => {
                    onPoligonoEmpreendimentos?.(poligonoGeojson);
                    setMostrarPopupFiltroPoligono(false);
                  }} 
                  
                >
                  Empreendimentos
                </button>

                <button
                  onClick={ () => {
                    onPoligonoComercios?.({
                      ...poligonoGeojson,
                      cnae: selectedCnae.map(c => c.value).join(","),
                    });
                    setMostrarPopupFiltroPoligono(false);
                  }}
                >
                  Comércios
                </button>

                <button
                  onClick={() => {
                    onPoligonoEmpreendimentos?.(poligonoGeojson);

                    onPoligonoComercios?.({
                      ...poligonoGeojson,
                      cnae: selectedCnae.map(c => c.value).join(","),
                    });

                    setMostrarPopupFiltroPoligono(false);
                    }}
                >
                  Ambos
                </button>

                <button
                  style={{ marginTop: 10 }}
                  onClick={() => setMostrarPopupFiltroPoligono(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

      {heatmapOn ? (
        <HeatMapEmpreendimentos initialViewState={viewState} imoveis={imoveis} municipios={municipios} />
      ) : (
        <div className="mapa-container">
          <Map
            {...viewState}
            mapboxAccessToken="pk.eyJ1IjoiZXZhbmRzb25jZXNhciIsImEiOiJjbWN3YXNtMjkwMGl2Mm5wd21mdGRlazA0In0.bvgSecEoO0w-PDKVfkcpjQ"
            mapStyle="mapbox://styles/mapbox/streets-v11"
            onMove={(evt) => setViewState(evt.viewState)}
            style={{ width: '100%', height: '100%' }}
            onLoad={handleMapLoad}
          >
            <Source
              id="imoveis"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: imoveis.map(imovel => ({
                  type: 'Feature',
                  properties: { id: imovel.id, title: imovel.nome },
                  geometry: { type: 'Point', coordinates: [imovel.longitude, imovel.latitude] },
                })),
              }}
            />
            {imoveis.map(imovel => {
              const Icon = getIconByTipo(imovel.tipo);
              return (
                <Marker key={imovel.id} longitude={imovel.longitude} latitude={imovel.latitude} anchor="bottom">
                  <div
                    title={imovel.nome}
                    style={{ display: 'inline-block', cursor: 'pointer' }}
                    onClick={() => setImovelSelecionado(imovel)}
                  >
                    <Icon
                      size={22}
                      color="white"
                      style={{ border: '2px solid #29306a', borderRadius: '50%', backgroundColor: '#29306a', padding: '2px' }}
                    />
                  </div>
                </Marker>
              );
            })}
            {mostrarLegenda && <LegendaCnae />}
            {comercios?.map((c: ComercioSimples) => {
              const color = getColorByCnae(c.cnae_codigo);
              const Icon = getIconByCnae(c.cnae_codigo);

              return (
                <Marker key={c.id} longitude={c.longitude} latitude={c.latitude}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      const currentZoom = mapRef.current?.getZoom() ?? 0;
                      if (currentZoom >= CLICKABLE_ZOOM) handleComercioClick(c);
                      else alert(`Dê zoom para ver detalhes do comércio (zoom mínimo: ${CLICKABLE_ZOOM})`);
                    }}
                  >
                    <Icon
                      size={22}
                      color="white"
                      style={{
                        border: `2px solid ${color}`,
                        borderRadius: '50%',
                        backgroundColor: color,
                        padding: '2px',
                        cursor:
                          (mapRef.current?.getZoom() ?? 0) >= CLICKABLE_ZOOM
                            ? 'pointer'
                            : 'not-allowed'
                      }}
                    />
                  </div>
                </Marker>
              );
            })}

            {poligonoGeojson && (
              <Source id="poligono-filtrado" type="geojson" data={limparGeojson(poligonoGeojson)}>
                <Layer
                  id="poligono-filtrado-fill"
                  type="fill"
                  paint={{
                    "fill-color": "#0080ff",
                    "fill-opacity": 0.2
                  }}
                />
                <Layer
                  id="poligono-filtrado-outline"
                  type="line"
                  paint={{
                    "line-color": "#0050aa",
                    "line-width": 2
                  }}
                />
              </Source>
            )}
          </Map>

          {comercioSelecionado && popupPos && (
            <div
              className="popup-comercio"
              style={{
                position: "absolute",
                left: popupPos.x,
                top: popupPos.y - 45,
                transform: "translate(-50%, -100%)",
                zIndex: 1000
              }}
            >
              <div className="popup-content">
                <h4>{comercioSelecionado.nome_fantasia}</h4>

                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/EstabelecimentoViewSet/detalhe?id=${comercioSelecionado.id}`);
                      setDetalhes(data);
                    } catch {
                      alert("Erro ao carregar detalhes.");
                    }
                  }}
                >
                  Mais detalhes
                </button>

                <button onClick={() => setComercioSelecionado(null)}>Fechar</button>
              </div>

              <div className="popup-arrow" />
            </div>
          )}


          {imovelSelecionado && (
            <Draggable nodeRef={cardRef} bounds="parent">
              <div ref={cardRef} className="mapa-info-card">
                <div className="mapa-info-header">
                  <h3 className="mapa-info-title">{imovelSelecionado.nome}</h3>
                  <button className="mapa-close-btn" onClick={() => setImovelSelecionado(null)}>×</button>
                </div>

                <ul className="mapa-info-list">
                  <li><strong>Construtora:</strong> {imovelSelecionado.construtora || '—'}</li>
                  <li><strong>Bairro:</strong> {imovelSelecionado.bairro || '—'}</li>
                  <li><strong>Tipo:</strong> {imovelSelecionado.tipo?.join(', ') || '—'}</li>
                </ul>

                <div className="mapa-price-row">
                  <strong>R$ {imovelSelecionado.preco?.toLocaleString('pt-BR') || '—'}</strong>
                  <span className="mapa-status-pill" style={{
                    backgroundColor: imovelSelecionado.status === 'Esgotado' ? '#ff4d4f' :
                                     imovelSelecionado.status === 'Disponível' ? '#44c3ff' : '#999'
                  }}>
                    {imovelSelecionado.status || 'Indisponível'}
                  </span>
                </div>

                <div className="mapa-units-row">
                  <small className="mapa-units-total">
                    {imovelSelecionado.unidadesTotais ? `${imovelSelecionado.unidadesTotais} unidades totais` : ''}
                  </small>
                  <div className="mapa-percent-sold">
                    {imovelSelecionado.vendidosPercent !== undefined ? `${imovelSelecionado.vendidosPercent}% vendido` : '—'}
                  </div>
                </div>

                {imovelSelecionado.unidadesTotais !== undefined && (
                  <div
                    className="mapa-progress-bar"
                    onMouseMove={(e) => handleProgressMouseMove(e, imovelSelecionado)}
                    onMouseEnter={() => setTooltipVisible(true)}
                    onMouseLeave={() => setTooltipVisible(false)}
                  >
                    <div className="mapa-progress-fill" style={{ width: `${imovelSelecionado.vendidosPercent || 0}%` }} />
                    <div className={`mapa-tooltip ${tooltipVisible ? 'visible' : ''}`} style={{ left: `${tooltipLeft}px` }}>
                      {tooltipText}
                    </div>
                  </div>
                )}
                <button 
                  onClick={() => {
                    navigate(`/empreendimentos/${imovelSelecionado.id}`);
                  }} 
                  className="mapa-goto-btn"
                >
                  Ir para empreendimento
                </button>
              </div>
            </Draggable>
          )}

          {/* --- CARD DE RESUMO --- */}
          {resumo && mostrarResumo && (
          <Draggable nodeRef={resumoRef} bounds="parent">
            <div ref={resumoRef} className="mapa-resumo-card">
              <div className="mapa-info-header">
                <h3 className="mapa-info-title">Resumo dos Empreendimentos</h3>
                <button className="mapa-close-btn" onClick={() => setMostrarResumo(false)}>×</button>
              </div>

              <ul className="mapa-info-list">
                <li><strong>Preço médio m²:</strong> R$ {resumo.preco_m2_medio.toLocaleString('pt-BR')}</li>
                <li><strong>Preço médio:</strong> R$ {resumo.preco_medio_produto.toLocaleString('pt-BR')}</li>
                <li><strong>Valor mínimo:</strong> R$ {resumo.valor_minimo.toLocaleString('pt-BR')}</li>
                <li><strong>Valor máximo:</strong> R$ {resumo.valor_maximo.toLocaleString('pt-BR')}</li>
                <li><strong>Área média:</strong> {resumo.area_media.toLocaleString('pt-BR')} m²</li>
                <li><strong>Unidades lançadas:</strong> {resumo.unidades_lancadas}</li>
                <li><strong>Unidades vendidas:</strong> {resumo.unidades_vendidas}</li>
                <li><strong>Total de unidades:</strong> {resumo.total_unidades}</li>
                <li><strong>VSO:</strong> {resumo.vso}%</li>
                <li><strong>Vendas mês atual:</strong> {resumo.vendas_mes_atual}</li>
              </ul>
            </div>
          </Draggable>
        )}
        </div>
      )}
    </div>
  );
};

export default MapaEmpreendimentos;
